import { defineAsyncComponent, reactive, toRaw } from "vue";
import { createColumns, createFieldFeeInfo, createFieldInvoiceInfo } from "./fee.section";
import {
    createAppPageHeaderTop,
    createManageExportCsv,
    createManageExtraCreate,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionPdf,
    createRowActionView,
    onClickReport
} from "../../../../utils/page";
import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickArchive,
    onClickCreate,
    onClickDelete,
    onClickEdit,
    onClickView
} from "../../../../utils/form";
import { createConditionAction, createConditionForm } from "./fee.condition";
import i18n from "../../../../i18n";
import { flatWhereGroup } from "../../../../utils/search";
import { createLoading } from "../../../../utils/loading";
import store from "../../../../store";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import { has } from "../../../../utils/menu-context";
import {
    createButton,
    createButtonActive,
    createButtonArchive,
    createButtonCreate,
    createButtonDelete,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { Col, Modal, notification, Row } from "ant-design-vue";
import { required, requiredWhen, validateError } from "../../../../utils/validate";
import lodash from "lodash";
import { openModalForm as openModalFormConfirmReceive } from "./modal/confirm-receive.logic";
import { openModalForm as openModalFormConfirmApprove } from "./modal/confirm-approve.logic";
import { modalStyleForm } from "../../../../utils/constant";

export const { isLoading, setIsLoading } = createLoading();
export const actionPrefix = "resource/v1/transaction/feeStatement";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callReportData = ({ data }) => store.dispatch(`${actionPrefix}/reportData`, { data });
export const callReportReceipt = ({ data }) => store.dispatch(`${actionPrefix}/reportReceipt`, { data });

export const callFeeStatus = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "FEE_STATUS", value: searchQuery });
export const callFeeType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "FEE_TYPE", value: searchQuery });
export const callBank = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "BANK", value: searchQuery });
export const callBankType = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "BANK_TYPE", value: searchQuery });
export const callRequestDoc = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "REQUEST_DOC", value: searchQuery });

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));
const CIFrame = defineAsyncComponent(() => import("../../../../components/iframe/CIFrame"));

export const FeeForm = defineAsyncComponent(() => import("./FeeForm"));

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...where, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, formParams }) => {
    const { viewOnly, routerPrefix } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
                .finally(() => setIsLoadingApp(false))
                .then(record => {
                    if (viewOnly) initForm({ props, data, refs });
                    else onClickView({ record, routerPrefix });
                });
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const openModalFeeForm = ({ props, title }) => {
    const refs = reactive({ form: null });
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleForm), {
            okButtonProps: { disabled: false },
            title: title || i18n.global.t("views.fee.feeDoc"),
            width: "500px",
            onOk: () => {
                options.bind.onCancel();
            },
        }),
        render: () => (<FeeForm ref={ (e) => {if (e) refs.form = e;} }{...props}></FeeForm>),
    });
    return options;
};

export const openModalForm = ({ bind, id }) => {
    const options = addModal({
        bind: lodash.merge(bind),
        render: () => (
            <CIFrame src={`${process.env.VUE_APP_PUBLIC_PATH}/app/fee/fees/${id}/view?isLayout=false`}></CIFrame>
        )
    });
    return options;
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**", "refRequest.refRequestType.*", "refRequest.refRequestDoc.*", "refRequest.refHazTemp.refUserCompany.*",
                "refKtb.*", "refKtbPaynet.*",// "refPain008.*", "refPain002.*", "refCamt054.*",
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: [
                {
                    name: "statuses",
                    asName: "lastStatus.waitPayment",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitPayment" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "refRequest.statuses",
                    asName: "lastStatus.waitReceive",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "waitReceive" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "refUserCompany.userBanks",
                    asName: "lastUserBank.active",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [
                        { name: "isTemp", type: "Boolean", operator: "=", value: "false" },
                        { name: "isActive", type: "Boolean", operator: "=", value: "true" }
                    ],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "attachments",
                    select: ["**"],
                    page: 0,
                    size: 100,
                    where: [{ name: "isActive", type: "Boolean", operator: "=", value: "true" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
                {
                    name: "serviceLogs",
                    select: ["**"],
                    page: 0,
                    size: 100,
                    sort: [{ name: "createDate", type: "ASC" }]
                },
                // {
                //     name: "ktbs",
                //     select: ["**"],
                //     page: 0,
                //     size: 1000,
                //     sort: [{ name: "reqDateTime", type: "DESC" }]
                // },
                // {
                //     name: "ktbs",
                //     asName: "ktb.payment",
                //     select: ["**"],
                //     page: 0,
                //     size: 1,
                //     where: [
                //         { name: "reqCommand", type: "String", operator: "=", value: "Payment" },
                //         { name: "resRespCode", type: "Integer", operator: "=", value: 0 },
                //         { name: "isActive", type: "Boolean", operator: "=", value: "true" }
                //     ],
                //     sort: [{ name: "reqDateTime", type: "DESC" }]
                // },
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};


export const createWhereFor = ({ props }) => {
    const { menuContext } = props;
    const userInfo = store.state.resource.auth.info?.user;
    const whereGroup = [];
    if (has({ menuContext, key: "forRequest" }) && !userInfo.refOfficerLv) {
        whereGroup.push([
            { name: "refRequest.createBy", type: "String", operator: "=", value: userInfo.username },
            { name: "refStatus.key", type: "String", operator: "!=", value: "draft" },
        ]);
    }
    if (userInfo.refOfficerLv?.key?.startsWith("OFFICE_")) {
        whereGroup.push([
            { name: "refRequest.refDepartment.id", type: "UUID", operator: "=", value: userInfo.refDepartment.id },
        ])
    }
    return [...flatWhereGroup({ where: whereGroup, logic: "OR" })];
};

export const createForm = () => {
    return {
        refRequest: null,
        receiptCode: null,
        feeCode: null,
        feeDate: null,
        dueDate: null,
        payDatetime: null,
        companyName: null,
        requestNumber1: null,
        requestNumber2: null,
        netAmount: null,
        refType: null,
        refStatus: null,
        refOfficer: null,
        customerName: null,
        requesterRemark: null,
        officerRemark: null,
        attachments: [],
        statuses: []
    }
}

export const createRules = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    return {
        refType: [required],
        payDatetime: [required],
        customerName: [required],
        [`${prefix}refBank`]: [
            requiredWhen({ when: () => ["1", "2"].includes(model.refType?.key) }),
            {
                validator: (rule, value) => {
                    if (["1", "2"].includes(model.refType?.key) && value?.key !== "006") {
                        return Promise.reject(i18n.global.t("error.form.ktbOnly"));
                    }
                    return Promise.resolve();
                },
                required: true,
                trigger: "change"
            }
        ],
        [`${prefix}branch`]: [requiredWhen({ when: () => ["1", "2"].includes(model.refType?.key) })],
        [`${prefix}branchCode`]: [requiredWhen({ when: () => ["1", "2"].includes(model.refType?.key) })],
        [`${prefix}refBankType`]: [requiredWhen({ when: () => ["1", "2"].includes(model.refType?.key) })],
        [`${prefix}bankAccount`]: [requiredWhen({ when: () => ["1", "2"].includes(model.refType?.key) })],
        [`${prefix}bankName`]: [requiredWhen({ when: () => ["1", "2"].includes(model.refType?.key) })],
        [`${prefix}attachments`]: [requiredWhen({ when: () => model.refType?.key === "3" })],
        [`${prefix}refOfficer`]: [requiredWhen({ when: () => model.refType?.key === "4" })],
    };
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${modelRef.feeCode || modelRef.requestNumber2 } (${modelRef.refStatus?.value})` : i18n.global.t("button.create");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, data, refs, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .then(() => initCheckKtbPaynetRsp({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = ({ data }) => {
    const promises = [callFeeStatus({})];
    return Promise.all(promises).then(arr => {
        data.statuses = arr[0];
    });
};

export const initCheckKtbPaynetRsp = ({ data }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    if (modelRef?.refKtbPaynet?.rspCode && modelRef.refKtbPaynet.rspCode !== "IC000") {
        notification["warning"]({ message: `[History] ${modelRef.refKtbPaynet.rspCode} ${modelRef.refKtbPaynet.rspMsg}` });
    }
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix } = props;
    state.columns = createColumns({ props })
    state.manages = [
        createManageSelect({ menuContext }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [
        createManageExtraCreate({ menuContext, onClick: () => onClickCreate({ routerPrefix }) }),
    ];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
        createRowActionPdf({
            bind: { class: "btn-primary", size: "small", icon: () => <CIcon file="printer.svg"></CIcon> },
            text: () => i18n.global.t("button.viewDocument"),
            callReport: ({ record }) => callReportData({ data: { id: record.id } }),
            fileName: "report.pdf",
            visible: ({ record }) => state.conditionAction.canReport({ props, record })
        })
    ];
};

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fieldsFeeInfo = createFieldFeeInfo({ props, data, refs, isLoading });
    data.fieldsInvoiceInfo = createFieldInvoiceInfo({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canReEdit = state.conditionAction.canReEdit({ props, record: initialModel });
    const canWaitPayment = state.conditionAction.canWaitPayment({ props, record: initialModel });
    const canWaitReceive = state.conditionAction.canWaitReceive({ props, record: initialModel });
    const canWaitApprove = state.conditionAction.canWaitApprove({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });

    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ props, data, refs }), classStyle: "m-1" }));
    }
    if (id && isActive && canWaitReceive) {
        const statusWaitReceive = data.statuses.find(status => status.key === "waitReceive");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWaitReceive?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWaitReceive?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    data.useForm.validate().then(() => {
                        Modal.confirm({
                            title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: statusWaitReceive?.attribute?.actionText }),
                            autoFocusButton: "cancel",
                            content: <div>{modelRef?.refType?.value || ""}</div>,
                            onOk: () => {
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        refStatus: statusWaitReceive,
                                    }
                                })
                            },
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    // re-status
    if (id && isActive && viewOnly && canWaitPayment) {
        const statusWaitPayment = data.statuses.find(status => status.key === "waitPayment");
        col1.push(
            createButton({
                text: i18n.global.t("button.reStatus"),
                classStyle: `m-1 btn-danger`,
                onClick: () => {
                    data.sendReceive = true;
                    data.useForm.validate().then(() => {
                        Modal.confirm({
                            title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.reStatus") }),
                            autoFocusButton: "cancel",
                            content: <div>{modelRef?.refType?.value || ""}</div>,
                            onOk: () => {
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        refStatus: statusWaitPayment,
                                    }
                                })
                            },
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && viewOnly && canReEdit) {
        const statusReEdit = data.statuses.find(status => status.key === "reEdit");
        col1.push(
            createButton({
                text: `${statusReEdit?.attribute?.actionText}`,
                classStyle: `m-1 ${statusReEdit?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendReceive = true;
                    data.useForm.validate().then(() => {
                        Modal.confirm({
                            title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: statusReEdit?.attribute?.actionText }),
                            autoFocusButton: "cancel",
                            content: <div>{modelRef?.refType?.value || ""}</div>,
                            onOk: () => {
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        refStatus: statusReEdit,
                                    }
                                })
                            },
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canWaitApprove) {
        const statusWaitApprove = data.statuses.find(status => status.key === "waitApprove");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusWaitApprove?.attribute?.actionText}`,
                classStyle: `m-1 ${statusWaitApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendApprove = true;
                    data.useForm.validate().then(() => {
                        openModalFormConfirmReceive({
                            onOk: record => {
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        refStatus: record.refStatus,
                                        refOfficer: store.state.resource.auth.info?.user,
                                    }
                                })
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove) {
        const statusApprove = data.statuses.find(status => status.key === "approve");
        col1.push(
            createButton({
                text: `${viewOnly ? "" : `${i18n.global.t("button.save")} & `}${statusApprove?.attribute?.actionText}`,
                classStyle: `m-1 ${statusApprove?.attribute?.btnClass}`,
                onClick: () => {
                    data.sendApprove = true;
                    data.useForm.validate().then(() => {
                        openModalFormConfirmApprove({
                            onOk: record => {
                                onClickSave({
                                    props,
                                    data,
                                    refs,
                                    formParams: {
                                        refStatus: record.refStatus,
                                        refOfficer: store.state.resource.auth.info?.user,
                                    }
                                })
                            }
                        });
                    });
                },
                icon: viewOnly ? null : <CIcon file="save.svg"></CIcon>
            })
        );
    }
    if (id && isActive && modelRef?.refStatus?.key !== "excludeFee") {
        col1.push(
            createButton({
                text: `${i18n.global.t("views.fee.feeDocDownload")}`,
                classStyle: "btn-purple",
                onClick: () => onClickReport({
                    callReport: () => callReportData({ data: { id } }),
                    fileName: modelRef?.feeCode ? `${modelRef?.feeCode}.pdf` : "report.pdf"
                }),
                icon: viewOnly ? null : <CIcon file="printer.svg"></CIcon>
            })
        );
    }
    if (id && isActive && canApprove && ["waitApprove"].includes(modelRef?.refStatus?.key)) {
        col1.push(
            createButton({
                text: `${i18n.global.t("views.fee.receiptDoc")} (${i18n.global.t("views.fee.preview")})`,
                classStyle: "btn-primary",
                onClick: () => onClickReport({
                    callReport: () => callReportReceipt({ data: { id } }),
                    fileName: modelRef?.receiptCode ? `${modelRef?.receiptCode}.pdf` : modelRef?.feeCode ? `${modelRef?.feeCode}.pdf` : "report.pdf"
                }),
                icon: viewOnly ? null : <CIcon file="printer.svg"></CIcon>
            })
        );
    }
    if (id && isActive && ["approve"].includes(modelRef?.refStatus?.key)) {
        col1.push(
            createButton({
                text: `${i18n.global.t("views.fee.receiptDocReal")}`,
                classStyle: "btn-success",
                onClick: () => {
                    const url = `${process.env.VUE_APP_API_REPORT_URL}/files/haz/fee/${id}/file-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`;
                    window.open(url);
                },
                icon: viewOnly ? null : <CIcon file="printer.svg"></CIcon>
            })
        );
        col1.push(
            createButton({
                text: `${i18n.global.t("views.fee.receiptDocCopy")}`,
                classStyle: "btn-success",
                onClick: () => {
                    const url = `${process.env.VUE_APP_API_REPORT_URL}/files/haz/fee/${id}/file-copy-result.pdf?contentDisposition=inline&_timestamp=${new Date().getTime()}`;
                    window.open(url);
                },
                icon: viewOnly ? null : <CIcon file="printer.svg"></CIcon>
            })
        );
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canArchive" })) {
        col2.push(
            createButtonArchive({
                onClick: () =>
                    onClickArchive({
                        record: { id },
                        callArchive,
                        setIsLoading: setIsLoadingApp,
                        onClickView: () => onClickView({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (!viewOnly && id && isActive && has({ menuContext, key: "canDelete" })) {
        col2.push(
            createButtonDelete({
                onClick: () => onClickDelete({ record: { id }, callDelete, setIsLoading: setIsLoadingApp, state, routerPrefix }),
                classStyle: "m-1"
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};


export const state = reactive({
    page: createPage({
        options: {
            select: ["**"],
            children: [
                {
                    name: "statuses",
                    asName: "lastStatus.approve",
                    select: ["**"],
                    page: 0,
                    size: 1,
                    where: [{ name: "refStatus.key", type: "String", operator: "=", value: "approve" }],
                    sort: [{ name: "createDate", type: "DESC" }]
                },
            ],
            sort: [/* { name: "refStatus.seq", type: "ASC" } ,*/ { name: "updateDate", type: "DESC" }]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});